// SSA Vantage — Shared Structured Policy Renderer
// Single source of truth for rendering structured_content JSON across all tiers.
// Used by: Global Admin library, Org Admin library, Client Policies Admin, Client Portal.

const NAVY = '#012438'
const ACCENT = '#3D8F8F'
const BODY_TEXT = '#2D3748'
const MUTED = '#64748B'
const LIGHT_BG = '#F8FAFC'
const BORDER = '#E2E8F0'
const FONT = 'Calibri, -apple-system, sans-serif'

const body = {
  fontSize: '11.5px',
  color: BODY_TEXT,
  lineHeight: 1.7,
  marginTop: '6px',
  marginBottom: '6px',
  fontFamily: FONT
}

const cellStyle = (alt) => ({
  padding: '6px 14px',
  fontSize: '11px',
  color: BODY_TEXT,
  fontFamily: FONT,
  backgroundColor: alt ? LIGHT_BG : '#FFFFFF',
  borderBottom: `1px solid ${BORDER}`,
  borderLeft: 'none',
  borderRight: 'none',
  borderTop: 'none'
})

const headerCell = {
  padding: '8px 14px',
  fontSize: '9px',
  fontWeight: 700,
  color: '#FFFFFF',
  fontFamily: FONT,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  backgroundColor: NAVY,
  textAlign: 'left',
  borderLeft: 'none',
  borderRight: 'none'
}

function SectionHeading({ number, title }) {
  return (
    <div style={{ marginTop: '32px', marginBottom: '14px', borderBottom: `2px solid ${ACCENT}`, paddingBottom: '6px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
      <span style={{ fontSize: '13px', fontWeight: 800, color: NAVY, fontFamily: FONT }}>{number}.</span>
      <h2 style={{ fontSize: '15px', fontWeight: 700, color: NAVY, fontFamily: FONT, margin: 0, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{title}</h2>
    </div>
  )
}

// Parse inline framework citations from text and render them styled
const FRAMEWORK_NAMES = ['NIST CSF', 'NIST SP 800-53', 'NIST 800-53', 'NIST SP 800-171', 'NIST 800-171', 'ISO 27001', 'ISO/IEC 27001', 'CIS Control', 'CIS Controls', 'SOC 2', 'HIPAA', 'PCI DSS', 'GLBA', 'FFIEC', 'CMMC', 'GDPR', 'CCPA', 'NYDFS', 'FERPA', 'DORA', 'CARF', 'FTC Safeguards', 'SEC Cyber', 'NIST AI RMF', 'ISO 42001', 'EU AI Act', 'FedRAMP', 'HITRUST', 'COBIT', 'NERC CIP', 'SWIFT CSCF']

function renderTextWithCitations(text) {
  if (!text) return null
  const result = []
  let remaining = text
  let safetyCount = 0

  while (remaining && safetyCount < 200) {
    safetyCount++
    const openIdx = remaining.indexOf('(')
    if (openIdx === -1) { result.push(remaining); break }

    let depth = 0
    let closeIdx = -1
    for (let i = openIdx; i < remaining.length; i++) {
      if (remaining[i] === '(') depth++
      if (remaining[i] === ')') {
        depth--
        if (depth === 0) { closeIdx = i; break }
      }
    }
    if (closeIdx === -1) { result.push(remaining); break }

    const endIdx = remaining[closeIdx + 1] === '.' ? closeIdx + 2 : closeIdx + 1
    const before = remaining.slice(0, openIdx)
    const paren = remaining.slice(openIdx, endIdx)
    remaining = remaining.slice(endIdx)

    if (before) result.push(before)

    const hasFramework = FRAMEWORK_NAMES.some(f => paren.toLowerCase().includes(f.toLowerCase()))
    if (hasFramework) {
      result.push({ citation: true, text: paren })
    } else {
      result.push(paren)
    }
  }

  if (result.length <= 1 && typeof result[0] === 'string') return text

  return result.map((part, i) => {
    if (typeof part === 'object' && part.citation) {
      return <span key={i} style={{ fontSize: '9.5px', color: MUTED, fontStyle: 'italic', fontFamily: FONT }}>{part.text}</span>
    }
    return part
  })
}

/**
 * StructuredPolicyRenderer — renders structured_content JSON as a formatted document.
 *
 * @param {object} props.structuredContent - The structured_content JSONB object
 * @param {object} [props.template] - Template/policy metadata (title, domain, status, framework_tags, version_number, created_at)
 * @param {array}  [props.versionHistory] - Version history rows for revision history table
 * @param {boolean} [props.showHeader=true] - Show the navy cover header
 * @param {boolean} [props.showFooter=true] - Show the footer
 * @param {boolean} [props.showRevisionHistory=true] - Show the revision history table
 * @param {string} [props.clientName] - Client name for "Prepared for" line
 */
function StructuredPolicyRenderer({
  structuredContent: sc,
  template = {},
  versionHistory,
  showHeader = true,
  showFooter = true,
  showRevisionHistory = true,
  clientName
}) {
  if (!sc) {
    return (
      <div style={{ padding: '64px 48px', textAlign: 'center' }}>
        <p style={{ ...body, color: MUTED }}>No structured content available.</p>
      </div>
    )
  }

  const dc = sc.document_control || {}

  return (
    <>
      {/* ═══ COVER HEADER ═══ */}
      {showHeader && (
        <div style={{ backgroundColor: NAVY, padding: '40px 52px 32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <div style={{ width: '32px', height: '2px', backgroundColor: ACCENT }} />
            <p style={{ color: ACCENT, fontSize: '10px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', fontFamily: FONT, margin: 0 }}>
              Sidewalk Security Advisors
            </p>
          </div>
          <h1 style={{ color: '#FFFFFF', fontSize: '26px', fontWeight: 700, lineHeight: 1.25, marginBottom: '6px', fontFamily: FONT }}>
            {template.title || template.domain || 'Policy Template'}
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '12px', fontFamily: FONT, marginBottom: clientName ? '8px' : '24px' }}>
            {template.domain || ''}
          </p>
          {clientName && (
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '11px', fontFamily: FONT, marginBottom: '24px', fontStyle: 'italic' }}>
              Prepared for {clientName}
            </p>
          )}
          <div style={{ height: '3px', backgroundColor: ACCENT, width: '60px' }} />
        </div>
      )}

      {/* ═══ DOCUMENT CONTROL ═══ */}
      <div style={{ padding: '20px 52px 16px', borderBottom: `1px solid ${BORDER}` }}>
        <p style={{ fontSize: '9px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: FONT, marginBottom: '10px' }}>Document Control</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px 24px' }}>
          {[
            ['Document ID', dc.policy_id || 'SSA-POL-XXX-001'],
            ['Version', dc.version || template.version_number || '1.0'],
            ['Status', template.status === 'published' ? 'Published' : 'Draft'],
            ['Classification', dc.classification || 'Confidential'],
            ['Effective Date', dc.effective_date || '[EFFECTIVE DATE]'],
            ['Review Cycle', dc.review_cycle || 'Annual'],
            ['Policy Owner', dc.owner || 'CISO'],
            ['Approved By', dc.approved_by || '[APPROVAL AUTHORITY]'],
          ].map(([label, value]) => (
            <div key={label}>
              <p style={{ fontSize: '8px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: FONT, marginBottom: '2px' }}>{label}</p>
              <p style={{ fontSize: '11px', color: NAVY, fontWeight: 400, fontFamily: FONT }}>{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ═══ FRAMEWORK ALIGNMENT ═══ */}
      {(template.framework_tags || []).length > 0 && (
        <div style={{ padding: '12px 52px', borderBottom: `1px solid ${BORDER}` }}>
          <p style={{ fontSize: '9px', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: FONT, marginBottom: '6px' }}>Regulatory Alignment</p>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {(template.framework_tags || []).map(tag => (
              <span key={tag} style={{ fontSize: '9px', fontWeight: 600, padding: '2px 10px', borderRadius: '3px', backgroundColor: NAVY, color: '#FFFFFF', fontFamily: FONT }}>{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* ═══ POLICY BODY ═══ */}
      <div style={{ padding: '8px 52px 52px', fontFamily: FONT }}>

        {/* 1. Purpose */}
        {sc.purpose && (
          <>
            <SectionHeading number="1" title="Purpose" />
            <p style={body}>{sc.purpose}</p>
          </>
        )}

        {/* 2. Scope */}
        {sc.scope && (
          <>
            <SectionHeading number="2" title="Scope" />
            {sc.scope.applies_to && <p style={body}><strong>2.1 Applicability.</strong> {sc.scope.applies_to}</p>}
            {sc.scope.exclusions && <p style={body}><strong>2.2 Exclusions.</strong> {sc.scope.exclusions}</p>}
          </>
        )}

        {/* 3. Definitions */}
        {sc.definitions?.length > 0 && (
          <>
            <SectionHeading number="3" title="Terms & Definitions" />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...headerCell, width: '180px' }}>Term</th>
                  <th style={headerCell}>Definition</th>
                </tr>
              </thead>
              <tbody>
                {sc.definitions.map((d, i) => (
                  <tr key={i}>
                    <td style={{ ...cellStyle(i % 2 === 0), fontWeight: 700, color: NAVY }}>{d.term}</td>
                    <td style={cellStyle(i % 2 === 0)}>{d.definition}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* 4. Policy Statements — nested numbered format */}
        {sc.policy_statements?.length > 0 && (
          <>
            <SectionHeading number="4" title="Policy Statements" />
            {sc.policy_statements.map((ps, i) => (
              <div key={i} style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '12px', fontWeight: 700, color: NAVY, marginTop: '12px', marginBottom: '6px', fontFamily: FONT }}>
                  <span style={{ color: NAVY, marginRight: '6px' }}>4.{i + 1}</span>
                  {ps.title}
                </p>
                {ps.content && <p style={{ ...body, paddingLeft: '24px' }}>{renderTextWithCitations(ps.content)}</p>}
                {ps.bullets?.length > 0 && (
                  <div style={{ paddingLeft: '28px', marginTop: '6px' }}>
                    {ps.bullets.map((b, j) => (
                      <div key={j} style={{ display: 'flex', gap: '8px', marginTop: '6px', marginBottom: '6px', alignItems: 'baseline' }}>
                        <span style={{ color: NAVY, fontWeight: 700, fontSize: '11.5px', fontFamily: FONT, minWidth: '48px', flexShrink: 0 }}>4.{i + 1}.{j + 1}</span>
                        <span style={{ ...body, marginTop: 0, marginBottom: 0 }}>{renderTextWithCitations(b)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* 5. Roles & Responsibilities */}
        {sc.roles_and_responsibilities?.length > 0 && (
          <>
            <SectionHeading number="5" title="Roles & Responsibilities" />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...headerCell, width: '200px' }}>Role</th>
                  <th style={headerCell}>Responsibilities</th>
                </tr>
              </thead>
              <tbody>
                {sc.roles_and_responsibilities.map((r, i) => (
                  <tr key={i}>
                    <td style={{ ...cellStyle(i % 2 === 0), fontWeight: 700, color: NAVY }}>{r.role}</td>
                    <td style={cellStyle(i % 2 === 0)}>{r.responsibilities}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* 6. Compliance & Enforcement */}
        {sc.compliance_and_enforcement && (
          <>
            <SectionHeading number="6" title="Compliance & Enforcement" />
            <p style={body}>{sc.compliance_and_enforcement}</p>
          </>
        )}

        {/* 7. Exceptions & Waivers */}
        <SectionHeading number="7" title="Exceptions & Waivers" />
        <p style={body}>
          Exceptions to this policy must be formally documented, approved by the Policy Owner or designee, and reviewed at each policy review cycle. Exception requests must include: a description of the non-compliance, business justification, risk assessment, compensating controls, and a proposed expiration date. All approved exceptions are logged in the organization's risk register.
        </p>

        {/* 8. Related Policies & Referenced Standards */}
        {sc.related_documents?.length > 0 && (
          <>
            <SectionHeading number="8" title="Related Policies & Referenced Standards" />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...headerCell, width: '40px' }}>#</th>
                  <th style={headerCell}>Document / Standard</th>
                </tr>
              </thead>
              <tbody>
                {sc.related_documents.map((d, i) => (
                  <tr key={i}>
                    <td style={{ ...cellStyle(i % 2 === 0), textAlign: 'center', color: MUTED, fontWeight: 600 }}>{i + 1}</td>
                    <td style={cellStyle(i % 2 === 0)}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* 9. Revision History */}
        {showRevisionHistory && (
          <>
            <SectionHeading number="9" title="Revision History" />
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...headerCell, width: '70px' }}>Version</th>
                  <th style={{ ...headerCell, width: '100px' }}>Date</th>
                  <th style={{ ...headerCell, width: '140px' }}>Author</th>
                  <th style={headerCell}>Description of Changes</th>
                </tr>
              </thead>
              <tbody>
                {(versionHistory || []).length > 0 ? (
                  versionHistory.map((v, i) => (
                    <tr key={i}>
                      <td style={cellStyle(i % 2 === 0)}>{v.version_number || '1.0'}</td>
                      <td style={cellStyle(i % 2 === 0)}>{v.created_at ? new Date(v.created_at).toLocaleDateString() : '\u2014'}</td>
                      <td style={cellStyle(i % 2 === 0)}>{v.created_by_name || 'SSA Advisory Team'}</td>
                      <td style={cellStyle(i % 2 === 0)}>{v.change_summary || 'Initial policy issuance'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td style={cellStyle(false)}>1.0</td>
                    <td style={cellStyle(false)}>{template.created_at ? new Date(template.created_at).toLocaleDateString() : '\u2014'}</td>
                    <td style={cellStyle(false)}>SSA Advisory Team</td>
                    <td style={cellStyle(false)}>Initial policy issuance</td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ═══ FOOTER ═══ */}
      {showFooter && (
        <>
          <div style={{ backgroundColor: NAVY, padding: '14px 52px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '8.5px', fontFamily: FONT, margin: 0 }}>
              &copy; {new Date().getFullYear()} Sidewalk Security Advisors. This document is confidential and proprietary. Unauthorized distribution is prohibited.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '8.5px', fontFamily: FONT, margin: 0 }}>
              {dc.policy_id || ''} &middot; v{dc.version || template.version_number || '1.0'} &middot; {dc.classification || 'Confidential'}
            </p>
          </div>
          <div style={{ backgroundColor: NAVY, padding: '14px 52px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '8.5px', fontFamily: FONT, margin: 0 }}>
              &copy; {new Date().getFullYear()} Sidewalk Security Advisors. This document is confidential and proprietary. Unauthorized distribution is prohibited.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '8.5px', fontFamily: FONT, margin: 0 }}>
              {dc.policy_id || ''} &middot; v{dc.version || template.version_number || '1.0'} &middot; End of Document
            </p>
          </div>
        </>
      )}
    </>
  )
}

export default StructuredPolicyRenderer
